import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { Button, ButtonMode } from '@components/atoms/Button';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useNotifications } from '@hooks/useNotifications';
import { useGetSpaceTypes } from '@services/useGetSpaceTypes';
import { useGetSpaceStatuses } from '@services/useGetSpaceStatuses';
import { useGetPropertyTypes } from '@services/useGetPropertyTypes';
import { useGetFeaturedTypes } from '@services/useGetFeaturedTypes';
import { useGetEnergyCertificates } from '@services/useGetEnergyCertificates';
import { useCreateSpace } from '@services/useCreateSpace';
import { useEditSpace } from '@services/useEditSpace';
import { useGetSpaceDetail } from '@services/useGetSpaceDetail';
import { useDeleteSpaceGallery } from '@services/useDeleteSpaceGallery';
import { useDeleteSpaceDocument } from '@services/useDeleteSpaceDocument';
import { useData } from '@hooks/useData';
import { GalleryItem, DocumentItem, ContactItem } from '@services/useGetSpaces';

/**
 * Parses a formatted price string (e.g., "9.975,00 €") to a numeric string.
 * Removes currency symbols, converts European format to standard decimal.
 */
const parseFormattedPrice = (formattedPrice: string | undefined | null): string => {
    if (!formattedPrice) return '';

    // Remove currency symbol and whitespace
    let cleaned = formattedPrice.replace(/[€$£\s]/g, '').trim();

    // Handle European format: "9.975,00" -> "9975.00"
    // Remove thousand separators (dots) and replace decimal comma with dot
    if (cleaned.includes(',')) {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }

    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? '' : parsed.toString();
};

type ContactType = 'email' | 'telefone' | 'facebook' | 'instagram' | 'form';

interface SpaceContact {
    type: ContactType;
    value: string;
}

interface SpaceFormErrors {
    general: string;
    spaceType: string[];
    spaceStatus: string[];
    title: string[];
    description: string[];
    address: string[];
    latitude: string[];
    longitude: string[];
    numberOfDoors: string[];
    numberOfFloors: string[];
    constructionYear: string[];
    energyCertificate: string[];
    propertyType: string[];
    featuredType: string[];
    published: string[];
    areaM2: string[];
    price: string[];
    images: string[];
    documents: string[];
    contacts: string[];
}

interface SpaceFormProps {
    edit: boolean;
    spaceUuid?: string;
}

export const SpaceForm: FC<SpaceFormProps> = ({ edit, spaceUuid }) => {
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const { t } = useTranslation();
    const { spaceDetail, isLoadingSpaceDetail } = useData();

    // Services for create/edit
    const {
        request: requestCreateSpace,
        errorMessage: errorMessageCreate,
        validationErrors: validationErrorsCreate,
        success: successCreate,
        lastResponseTime: lastCreate,
        createdSpace,
    } = useCreateSpace();

    const {
        request: requestEditSpace,
        errorMessage: errorMessageEdit,
        validationErrors: validationErrorsEdit,
        success: successEdit,
        successMessage: successMessageEdit,
        lastResponseTime: lastEdit,
    } = useEditSpace();

    const { request: requestSpaceDetail } = useGetSpaceDetail();

    // Delete services for images and documents
    const { request: requestDeleteGallery, loading: loadingDeleteGallery } = useDeleteSpaceGallery();
    const { request: requestDeleteDocument, loading: loadingDeleteDocument } = useDeleteSpaceDocument();

    // Services for dropdowns
    const { request: fetchSpaceTypes, spaceTypes, loading: loadingSpaceTypes } = useGetSpaceTypes();
    const { request: fetchSpaceStatuses, spaceStatuses, loading: loadingSpaceStatuses } = useGetSpaceStatuses();
    const { request: fetchPropertyTypes, propertyTypes, loading: loadingPropertyTypes } = useGetPropertyTypes();
    const { request: fetchFeaturedTypes, featuredTypes, loading: loadingFeaturedTypes } = useGetFeaturedTypes();
    const {
        request: fetchEnergyCertificates,
        energyCertificates,
        loading: loadingEnergyCertificates,
    } = useGetEnergyCertificates();

    const translations = {
        spaceTypeLabel: t('space-form.space-type.label'),
        spaceStatusLabel: t('space-form.space-status.label'),
        titleLabel: t('space-form.title.label'),
        descriptionLabel: t('space-form.description.label'),
        addressLabel: t('space-form.address.label'),
        latitudeLabel: t('space-form.latitude.label'),
        longitudeLabel: t('space-form.longitude.label'),
        numberOfDoorsLabel: t('space-form.number-of-doors.label'),
        numberOfFloorsLabel: t('space-form.number-of-floors.label'),
        constructionYearLabel: t('space-form.construction-year.label'),
        energyCertificateLabel: t('space-form.energy-certificate.label'),
        propertyTypeLabel: t('space-form.property-type.label'),
        featuredTypeLabel: t('space-form.featured-type.label'),
        publishedLabel: t('space-form.published.label'),
        areaM2Label: t('space-form.area-m2.label'),
        priceLabel: t('space-form.price.label'),
        // Error messages
        spaceTypeRequiredError: t('space-form.space-type.required-error'),
        spaceStatusRequiredError: t('space-form.space-status.required-error'),
        titleRequiredError: t('space-form.title.required-error'),
        descriptionRequiredError: t('space-form.description.required-error'),
        addressRequiredError: t('space-form.address.required-error'),
        latitudeRequiredError: t('space-form.latitude.required-error'),
        longitudeRequiredError: t('space-form.longitude.required-error'),
        numberOfDoorsRequiredError: t('space-form.number-of-doors.required-error'),
        numberOfFloorsRequiredError: t('space-form.number-of-floors.required-error'),
        constructionYearRequiredError: t('space-form.construction-year.required-error'),
        energyCertificateRequiredError: t('space-form.energy-certificate.required-error'),
        propertyTypeRequiredError: t('space-form.property-type.required-error'),
        featuredTypeRequiredError: t('space-form.featured-type.required-error'),
        publishedRequiredError: t('space-form.published.required-error'),
        areaM2RequiredError: t('space-form.area-m2.required-error'),
        priceRequiredError: t('space-form.price.required-error'),
        // Other
        submitButton: t('space-form.submit.button'),
        default: t('select-default-option'),
        publishedYes: t('space-form.published.yes'),
        publishedNo: t('space-form.published.no'),
        // File uploads
        imagesLabel: t('space-form.images.label'),
        imagesHint: t('space-form.images.hint'),
        imagesExisting: t('space-form.images.existing'),
        imagesNew: t('space-form.images.new'),
        documentsLabel: t('space-form.documents.label'),
        documentsHint: t('space-form.documents.hint'),
        documentsExisting: t('space-form.documents.existing'),
        documentsNew: t('space-form.documents.new'),
        removeFile: t('space-form.remove-file'),
        // Contacts
        contactsLabel: t('space-form.contacts.label'),
        contactsHint: t('space-form.contacts.hint'),
        addContact: t('space-form.contacts.add'),
        contactTypeEmail: t('space-form.contacts.type.email'),
        contactTypeTelefone: t('space-form.contacts.type.telefone'),
        contactTypeFacebook: t('space-form.contacts.type.facebook'),
        contactTypeInstagram: t('space-form.contacts.type.instagram'),
        contactTypeForm: t('space-form.contacts.type.form'),
        contactValuePlaceholder: t('space-form.contacts.value-placeholder'),
        // Delete confirmations
        confirmDeleteImage: t('space-form.confirm-delete-image'),
        confirmDeleteDocument: t('space-form.confirm-delete-document'),
    };

    // Fetch dropdown data on mount
    useEffect(() => {
        fetchSpaceTypes();
        fetchSpaceStatuses();
        fetchPropertyTypes();
        fetchFeaturedTypes();
        fetchEnergyCertificates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch space detail for edit mode
    useEffect(() => {
        if (edit && spaceUuid) {
            requestSpaceDetail(spaceUuid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edit, spaceUuid]);

    const formRef = useRef<HTMLFormElement>(null);

    // Form state
    const [spaceType, setSpaceType] = useState<string>('');
    const [spaceStatus, setSpaceStatus] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [latitude, setLatitude] = useState<string>('');
    const [longitude, setLongitude] = useState<string>('');
    const [numberOfDoors, setNumberOfDoors] = useState<string>('');
    const [numberOfFloors, setNumberOfFloors] = useState<string>('');
    const [constructionYear, setConstructionYear] = useState<string>('');
    const [energyCertificate, setEnergyCertificate] = useState<string>('');
    const [propertyType, setPropertyType] = useState<string>('');
    const [featuredType, setFeaturedType] = useState<string>('');
    const [published, setPublished] = useState<string>('');
    const [areaM2, setAreaM2] = useState<string>('');
    const [price, setPrice] = useState<string>('');
    const [images, setImages] = useState<File[]>([]);
    const [documents, setDocuments] = useState<File[]>([]);
    const [contacts, setContacts] = useState<SpaceContact[]>([]);

    // Existing files from API (edit mode)
    const [existingImages, setExistingImages] = useState<GalleryItem[]>([]);
    const [existingDocuments, setExistingDocuments] = useState<DocumentItem[]>([]);

    // Populate form fields in edit mode
    useEffect(() => {
        if (edit && spaceDetail?.data) {
            const space = spaceDetail.data;
            setSpaceType(space.spaceType?.uuid || '');
            setSpaceStatus(space.spaceStatus?.uuid || '');
            setTitle(space.title || '');
            setDescription(space.description || '');
            setAddress(space.address || '');
            setLatitude(space.latitude || '');
            setLongitude(space.longitude || '');
            setNumberOfDoors(space.numberOfDoors?.toString() || '');
            setNumberOfFloors(space.numberOfFloors?.toString() || '');
            setConstructionYear(space.constructionYear?.toString() || '');
            setEnergyCertificate(space.energyCertificate?.uuid || '');
            setPropertyType(space.spaceTypeCategory?.key || '');
            setFeaturedType(space.featuredType?.key || '');
            setPublished(space.isActive ? '1' : '0');
            setAreaM2(space.areaM2 || '');
            setPrice(space.price ? parseFormattedPrice(space.price) : '');

            // Load existing files
            setExistingImages(space.galleries || []);
            setExistingDocuments(space.documents || []);

            // Load existing contacts
            if (space.contacts && space.contacts.length > 0) {
                setContacts(
                    space.contacts.map((c: ContactItem) => ({
                        type: c.type,
                        value: c.value,
                    })),
                );
            }
        }
    }, [edit, spaceDetail]);

    // Errors state
    const [errors, setErrors] = useState<SpaceFormErrors>({
        general: '',
        spaceType: [],
        spaceStatus: [],
        title: [],
        description: [],
        address: [],
        latitude: [],
        longitude: [],
        numberOfDoors: [],
        numberOfFloors: [],
        constructionYear: [],
        energyCertificate: [],
        propertyType: [],
        featuredType: [],
        published: [],
        areaM2: [],
        price: [],
        images: [],
        documents: [],
        contacts: [],
    });

    // Build options for selects
    const spaceTypeOptions = [
        { value: '', text: translations.default },
        ...spaceTypes.map((type) => ({
            value: type.uuid,
            text: type.name,
        })),
    ];

    const spaceStatusOptions = [
        { value: '', text: translations.default },
        ...spaceStatuses.map((status) => ({
            value: status.uuid,
            text: status.name,
        })),
    ];

    const propertyTypeOptions = [
        { value: '', text: translations.default },
        ...propertyTypes.map((type) => ({
            value: type.key,
            text: type.value,
        })),
    ];

    const featuredTypeOptions = [
        { value: '', text: translations.default },
        ...featuredTypes.map((type) => ({
            value: type.key,
            text: type.value,
        })),
    ];

    const energyCertificateOptions = [
        { value: '', text: translations.default },
        ...energyCertificates.map((cert) => ({
            value: cert.uuid,
            text: cert.code,
        })),
    ];

    const publishedOptions = [
        { value: '', text: translations.default },
        { value: '1', text: translations.publishedYes },
        { value: '0', text: translations.publishedNo },
    ];

    const validateForm = (): SpaceFormErrors => {
        const newErrors: SpaceFormErrors = {
            general: '',
            spaceType: [],
            spaceStatus: [],
            title: [],
            description: [],
            address: [],
            latitude: [],
            longitude: [],
            numberOfDoors: [],
            numberOfFloors: [],
            constructionYear: [],
            energyCertificate: [],
            propertyType: [],
            featuredType: [],
            published: [],
            areaM2: [],
            price: [],
            images: [],
            documents: [],
            contacts: [],
        };

        !spaceType && newErrors.spaceType.push(translations.spaceTypeRequiredError);
        !spaceStatus && newErrors.spaceStatus.push(translations.spaceStatusRequiredError);
        !title.trim() && newErrors.title.push(translations.titleRequiredError);
        !energyCertificate && newErrors.energyCertificate.push(translations.energyCertificateRequiredError);
        !propertyType && newErrors.propertyType.push(translations.propertyTypeRequiredError);
        !featuredType && newErrors.featuredType.push(translations.featuredTypeRequiredError);
        !published && newErrors.published.push(translations.publishedRequiredError);

        setErrors(newErrors);
        return newErrors;
    };

    const hasNoErrors = (errors: SpaceFormErrors): boolean => {
        return (
            errors.spaceType.length === 0 &&
            errors.spaceStatus.length === 0 &&
            errors.title.length === 0 &&
            errors.energyCertificate.length === 0 &&
            errors.propertyType.length === 0 &&
            errors.featuredType.length === 0 &&
            errors.published.length === 0
        );
    };

    const handleOnClickSaveSpace = () => {
        const validationErrors = validateForm();

        if (hasNoErrors(validationErrors)) {
            formRef?.current?.requestSubmit();
        }
    };

    // Delete existing image with confirmation
    const handleDeleteImage = async (imageUuid: string) => {
        if (window.confirm(translations.confirmDeleteImage)) {
            const result = await requestDeleteGallery(imageUuid);
            if (result?.success) {
                setExistingImages(existingImages.filter((i) => i.uuid !== imageUuid));
                showDefaultNotification({ type: 'success', message: result.message });
            }
        }
    };

    // Delete existing document with confirmation
    const handleDeleteDocument = async (docUuid: string) => {
        if (window.confirm(translations.confirmDeleteDocument)) {
            const result = await requestDeleteDocument(docUuid);
            if (result?.success) {
                setExistingDocuments(existingDocuments.filter((d) => d.uuid !== docUuid));
                showDefaultNotification({ type: 'success', message: result.message });
            }
        }
    };

    const submitForm = async () => {
        const formData = new FormData();

        // Add text fields
        formData.append('space_type_uuid', spaceType);
        formData.append('space_status_uuid', spaceStatus);
        formData.append('title', title);
        formData.append('description', description);
        formData.append('address', address);
        formData.append('latitude', latitude);
        formData.append('longitude', longitude);
        formData.append('number_of_doors', numberOfDoors);
        formData.append('number_of_floors', numberOfFloors);
        formData.append('construction_year', constructionYear);
        formData.append('certificate_energy_uuid', energyCertificate);
        formData.append('space_type', propertyType);
        formData.append('featured_type', featuredType);
        formData.append('is_active', published === '1' ? '1' : '0');
        formData.append('area_m2', areaM2);
        formData.append('price', price);

        // Add image files
        images.forEach((file) => formData.append('images[]', file));

        // Add document files
        documents.forEach((file) => formData.append('documents[]', file));

        // Add contacts
        contacts.forEach((contact, index) => {
            formData.append(`contacts[${index}][type]`, contact.type);
            formData.append(`contacts[${index}][value]`, contact.value);
        });

        if (edit && spaceUuid) {
            // For edit, we need to use POST with _method override for Laravel
            formData.append('_method', 'PUT');
            requestEditSpace(spaceUuid, formData).then();
        } else {
            requestCreateSpace(formData).then();
        }
    };

    // Handle create response
    useEffect(() => {
        if (lastCreate) {
            if (errorMessageCreate || validationErrorsCreate) {
                // Collect image, document and contact errors from validation
                const imageErrors: string[] = [];
                const documentErrors: string[] = [];
                const contactErrors: string[] = [];
                if (validationErrorsCreate) {
                    Object.keys(validationErrorsCreate).forEach((key) => {
                        if (key.startsWith('images.')) {
                            imageErrors.push(...validationErrorsCreate[key]);
                        }
                        if (key.startsWith('documents.')) {
                            documentErrors.push(...validationErrorsCreate[key]);
                        }
                        if (key.startsWith('contacts.')) {
                            contactErrors.push(...validationErrorsCreate[key]);
                        }
                    });
                }

                setErrors({
                    general: errorMessageCreate || '',
                    spaceType: validationErrorsCreate?.space_type_uuid || [],
                    spaceStatus: validationErrorsCreate?.space_status_uuid || [],
                    title: validationErrorsCreate?.title || [],
                    description: validationErrorsCreate?.description || [],
                    address: validationErrorsCreate?.address || [],
                    latitude: validationErrorsCreate?.latitude || [],
                    longitude: validationErrorsCreate?.longitude || [],
                    numberOfDoors: validationErrorsCreate?.number_of_doors || [],
                    numberOfFloors: validationErrorsCreate?.number_of_floors || [],
                    constructionYear: validationErrorsCreate?.construction_year || [],
                    energyCertificate: validationErrorsCreate?.certificate_energy_uuid || [],
                    propertyType: validationErrorsCreate?.space_type || [],
                    featuredType: validationErrorsCreate?.featured_type || [],
                    published: validationErrorsCreate?.is_active || [],
                    areaM2: validationErrorsCreate?.area_m2 || [],
                    price: validationErrorsCreate?.price || [],
                    images: imageErrors,
                    documents: documentErrors,
                    contacts: contactErrors,
                });
            } else if (successCreate && createdSpace) {
                showDefaultNotification({ type: 'success', message: t('space-form.create.success') });
                navigate(`/${SITE_URLS.mySpaces}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCreate, createdSpace]);

    // Handle edit response
    useEffect(() => {
        if (lastEdit) {
            if (errorMessageEdit || validationErrorsEdit) {
                // Collect image, document and contact errors from validation
                const imageErrors: string[] = [];
                const documentErrors: string[] = [];
                const contactErrors: string[] = [];
                if (validationErrorsEdit) {
                    Object.keys(validationErrorsEdit).forEach((key) => {
                        if (key.startsWith('images.')) imageErrors.push(...validationErrorsEdit[key]);
                        if (key.startsWith('documents.')) documentErrors.push(...validationErrorsEdit[key]);
                        if (key.startsWith('contacts.')) contactErrors.push(...validationErrorsEdit[key]);
                    });
                }

                setErrors({
                    general: errorMessageEdit || '',
                    spaceType: validationErrorsEdit?.space_type_uuid || [],
                    spaceStatus: validationErrorsEdit?.space_status_uuid || [],
                    title: validationErrorsEdit?.title || [],
                    description: validationErrorsEdit?.description || [],
                    address: validationErrorsEdit?.address || [],
                    latitude: validationErrorsEdit?.latitude || [],
                    longitude: validationErrorsEdit?.longitude || [],
                    numberOfDoors: validationErrorsEdit?.number_of_doors || [],
                    numberOfFloors: validationErrorsEdit?.number_of_floors || [],
                    constructionYear: validationErrorsEdit?.construction_year || [],
                    energyCertificate: validationErrorsEdit?.certificate_energy_uuid || [],
                    propertyType: validationErrorsEdit?.space_type || [],
                    featuredType: validationErrorsEdit?.featured_type || [],
                    published: validationErrorsEdit?.is_active || [],
                    areaM2: validationErrorsEdit?.area_m2 || [],
                    price: validationErrorsEdit?.price || [],
                    images: imageErrors,
                    documents: documentErrors,
                    contacts: contactErrors,
                });
            } else if (successEdit) {
                showDefaultNotification({
                    type: 'success',
                    message: successMessageEdit || t('space-form.edit.success'),
                });
                // Clear new files after successful edit to prevent duplicate uploads
                setImages([]);
                setDocuments([]);
                // Refresh space detail
                if (spaceUuid) {
                    requestSpaceDetail(spaceUuid);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastEdit]);

    const isLoading = edit && isLoadingSpaceDetail;
    const isLoadingDropdowns = loadingSpaceTypes || loadingSpaceStatuses || loadingPropertyTypes || loadingFeaturedTypes || loadingEnergyCertificates;

    return (
        <Form formRef={formRef} onSubmit={() => submitForm()}>
            <>
                {errors.general && (
                    <>
                        <ErrorMessages messages={[errors.general]} />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                )}
            </>

            <div className="row">
                {/* Tipo de espaço */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.spaceTypeLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoading || loadingSpaceTypes}
                            options={spaceTypeOptions}
                            value={spaceType}
                            onChange={(value) => setSpaceType(value)}
                        />
                        {errors.spaceType.length > 0 && <ErrorMessages messages={errors.spaceType} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Estado */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.spaceStatusLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoading || loadingSpaceStatuses}
                            options={spaceStatusOptions}
                            value={spaceStatus}
                            onChange={(value) => setSpaceStatus(value)}
                        />
                        {errors.spaceStatus.length > 0 && <ErrorMessages messages={errors.spaceStatus} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Título */}
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={`${translations.titleLabel}*`}
                        value={title}
                        onChange={(value) => setTitle(value)}
                        disabled={isLoading}
                        state={errors.title.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.title}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Descrição */}
                <div className="col-12">
                    <Input
                        type={InputTypes.textarea}
                        variant={InputVariants.labeledInput}
                        label={translations.descriptionLabel}
                        value={description}
                        onChange={(value) => setDescription(value)}
                        disabled={isLoading}
                        state={errors.description.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.description}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Endereço */}
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={translations.addressLabel}
                        value={address}
                        onChange={(value) => setAddress(value)}
                        disabled={isLoading}
                        state={errors.address.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.address}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Latitude */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={translations.latitudeLabel}
                        value={latitude}
                        onChange={(value) => setLatitude(value)}
                        disabled={isLoading}
                        state={errors.latitude.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.latitude}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Longitude */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={translations.longitudeLabel}
                        value={longitude}
                        onChange={(value) => setLongitude(value)}
                        disabled={isLoading}
                        state={errors.longitude.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.longitude}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Número de portas */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={translations.numberOfDoorsLabel}
                        value={numberOfDoors}
                        onChange={(value) => setNumberOfDoors(value)}
                        disabled={isLoading}
                        state={errors.numberOfDoors.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.numberOfDoors}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Número de andares */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={translations.numberOfFloorsLabel}
                        value={numberOfFloors}
                        onChange={(value) => setNumberOfFloors(value)}
                        disabled={isLoading}
                        state={errors.numberOfFloors.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.numberOfFloors}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Ano de construção */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={translations.constructionYearLabel}
                        value={constructionYear}
                        onChange={(value) => setConstructionYear(value)}
                        disabled={isLoading}
                        state={errors.constructionYear.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.constructionYear}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Certificado energético */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.energyCertificateLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoading || loadingEnergyCertificates}
                            options={energyCertificateOptions}
                            value={energyCertificate}
                            onChange={(value) => setEnergyCertificate(value)}
                        />
                        {errors.energyCertificate.length > 0 && <ErrorMessages messages={errors.energyCertificate} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Tipo de propriedade */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.propertyTypeLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoading || loadingPropertyTypes}
                            options={propertyTypeOptions}
                            value={propertyType}
                            onChange={(value) => setPropertyType(value)}
                        />
                        {errors.propertyType.length > 0 && <ErrorMessages messages={errors.propertyType} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Tipo de destaque */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.featuredTypeLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoading || loadingFeaturedTypes}
                            options={featuredTypeOptions}
                            value={featuredType}
                            onChange={(value) => setFeaturedType(value)}
                        />
                        {errors.featuredType.length > 0 && <ErrorMessages messages={errors.featuredType} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Publicado */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.publishedLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoading}
                            options={publishedOptions}
                            value={published}
                            onChange={(value) => setPublished(value)}
                        />
                        {errors.published.length > 0 && <ErrorMessages messages={errors.published} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Área m2 */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={translations.areaM2Label}
                        value={areaM2}
                        onChange={(value) => setAreaM2(value)}
                        disabled={isLoading}
                        state={errors.areaM2.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.areaM2}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Preço */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={translations.priceLabel}
                        value={price}
                        onChange={(value) => setPrice(value)}
                        disabled={isLoading}
                        state={errors.price.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.price}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Imagens */}
                <div className="col-12">
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Typography color="muted" fontSize="12">
                        <>{translations.imagesLabel}</>
                    </Typography>
                    <Typography color="muted" fontSize="10">
                        <>{translations.imagesHint}</>
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    {/* Existing images (edit mode) */}
                    {existingImages.length > 0 && (
                        <>
                            <Typography color="muted" fontSize="10">
                                <strong>{translations.imagesExisting}</strong>
                            </Typography>
                            <div className="row mt-2 mb-3">
                                {existingImages.map((image) => (
                                    <div key={image.uuid} className="col-6 col-md-3 mb-2">
                                        <div className="position-relative">
                                            <img
                                                src={image.url}
                                                alt={image.name || 'Imagem'}
                                                className="img-thumbnail w-100"
                                                style={{ height: '100px', objectFit: 'cover' }}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                                onClick={() => handleDeleteImage(image.uuid)}
                                                disabled={isLoading || loadingDeleteGallery}
                                                title={translations.removeFile}
                                            >
                                                <i className="fa-solid fa-times"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* New images upload */}
                    {edit &&
                        <Typography color="muted" fontSize="10"><strong>{translations.imagesNew}</strong></Typography>}
                    <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/png,image/jpg,image/gif"
                        disabled={isLoading}
                        onChange={(e) => {
                            if (e.target.files) {
                                setImages([...images, ...Array.from(e.target.files)]);
                            }
                        }}
                        className="form-control"
                    />
                    {images.length > 0 && (
                        <div className="mt-2">
                            {images.map((file, index) => (
                                <div key={index} className="d-flex align-items-center gap-2 mb-1">
                                    <Typography fontSize="12">{file.name}</Typography>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => setImages(images.filter((_, i) => i !== index))}
                                    >
                                        {translations.removeFile}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {errors.images.length > 0 && <ErrorMessages messages={errors.images} />}
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Documentos */}
                <div className="col-12">
                    <Typography color="muted" fontSize="12">
                        <>{translations.documentsLabel}</>
                    </Typography>
                    <Typography color="muted" fontSize="10">
                        <>{translations.documentsHint}</>
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    {/* Existing documents (edit mode) */}
                    {existingDocuments.length > 0 && (
                        <>
                            <Typography color="muted" fontSize="10">
                                <strong>{translations.documentsExisting}</strong>
                            </Typography>
                            <div className="mt-2 mb-3">
                                {existingDocuments.map((doc) => (
                                    <div key={doc.uuid} className="d-flex align-items-center gap-2 mb-1">
                                        <i className="fa-solid fa-file-pdf text-danger"></i>
                                        <a href={doc.url} target="_blank" rel="noopener noreferrer"
                                           className="text-decoration-none">
                                            <Typography fontSize="12">{doc.name}</Typography>
                                        </a>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDeleteDocument(doc.uuid)}
                                            disabled={isLoading || loadingDeleteDocument}
                                        >
                                            {translations.removeFile}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* New documents upload */}
                    {edit && <Typography color="muted"
                                         fontSize="10"><strong>{translations.documentsNew}</strong></Typography>}
                    <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        disabled={isLoading}
                        onChange={(e) => {
                            if (e.target.files) {
                                setDocuments([...documents, ...Array.from(e.target.files)]);
                            }
                        }}
                        className="form-control"
                    />
                    {documents.length > 0 && (
                        <div className="mt-2">
                            {documents.map((file, index) => (
                                <div key={index} className="d-flex align-items-center gap-2 mb-1">
                                    <Typography fontSize="12">{file.name}</Typography>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => setDocuments(documents.filter((_, i) => i !== index))}
                                    >
                                        {translations.removeFile}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {errors.documents.length > 0 && <ErrorMessages messages={errors.documents} />}
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* Contatos */}
                <div className="col-12">
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Typography color="muted" fontSize="12">
                        <>{translations.contactsLabel}</>
                    </Typography>
                    <Typography color="muted" fontSize="10">
                        <>{translations.contactsHint}</>
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    {contacts.map((contact, index) => (
                        <div key={index} className="row mb-2 align-items-center">
                            <div className="col-md-3 col-12 mb-2 mb-md-0">
                                <Select
                                    classes="w-100"
                                    type={SelectType.variant1}
                                    disabled={isLoading}
                                    options={[
                                        { value: 'email', text: translations.contactTypeEmail },
                                        { value: 'telefone', text: translations.contactTypeTelefone },
                                        { value: 'facebook', text: translations.contactTypeFacebook },
                                        { value: 'instagram', text: translations.contactTypeInstagram },
                                        { value: 'form', text: translations.contactTypeForm },
                                    ]}
                                    value={contact.type}
                                    onChange={(value) => {
                                        const newContacts = [...contacts];
                                        newContacts[index] = { ...newContacts[index], type: value as ContactType };
                                        setContacts(newContacts);
                                    }}
                                />
                            </div>
                            <div className="col-md-7 col-9">
                                <Input
                                    type={
                                        contact.type === 'email'
                                            ? InputTypes.email
                                            : contact.type === 'telefone'
                                                ? InputTypes.tel
                                                : InputTypes.text
                                    }
                                    variant={InputVariants.regular}
                                    placeholder={translations.contactValuePlaceholder}
                                    value={contact.value}
                                    onChange={(value) => {
                                        const newContacts = [...contacts];
                                        newContacts[index] = { ...newContacts[index], value };
                                        setContacts(newContacts);
                                    }}
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="col-md-2 col-3">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger w-100"
                                    onClick={() => setContacts(contacts.filter((_, i) => i !== index))}
                                    disabled={isLoading}
                                >
                                    {translations.removeFile}
                                </button>
                            </div>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setContacts([...contacts, { type: 'email', value: '' }])}
                        disabled={isLoading}
                    >
                        <i className="fa-solid fa-plus me-1"></i>
                        {translations.addContact}
                    </button>

                    {errors.contacts.length > 0 && (
                        <>
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            <ErrorMessages messages={errors.contacts} />
                        </>
                    )}
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
            </div>

            {/* Submit */}
            <div className="col-12 text-end">
                <Button
                    icon="fa-solid fa-floppy-disk"
                    label={translations.submitButton}
                    mode={ButtonMode.primary}
                    disabled={isLoading || isLoadingDropdowns}
                    onClick={() => handleOnClickSaveSpace()}
                />
            </div>
        </Form>
    );
};
